'use client'

import { useMergeRefs } from '@floating-ui/react'
import React, {
  FC,
  HTMLProps,
  ReactElement,
  isValidElement,
  cloneElement
} from 'react'

import { ArrowDown } from '../../icons/arrow-down'
import { cn } from '../../utils/cn'
import { useSelectContext } from './select'

interface SelectTriggerProps extends HTMLProps<HTMLElement> {
  asChild?: boolean
  value?: string
  disabled?: boolean
  placeholder?: string
  children?: ReactElement<$TSFixMe>
}

export const SelectTrigger: FC<SelectTriggerProps> = ({
  children,
  asChild,
  placeholder,
  className,
  value,
  ref,
  ...props
}) => {
  const { refs, getReferenceProps, isOpen } = useSelectContext()

  /*
   * Note it is needed for managing tooltip with other floating components
   * for example Tooltip with Dropdown, Combobox, etc.
   */
  const mergedRef = useMergeRefs([
    (children as $TSFixMe)?.ref,
    ref,
    refs.setReference
  ])

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      getReferenceProps({
        ...props,
        ...(children.props || {}),
        ref: mergedRef,
        className,
        'data-state': isOpen ? 'open' : 'closed'
      } as object)
    )
  }

  return (
    <button
      tabIndex={0}
      {...getReferenceProps({
        ...props,
        ref: mergedRef
      })}
      className={cn(
        'flex h-10 w-full items-center rounded-md border border-solid border-gray-300 text-sm',
        'justify-between gap-x-3 px-3 transition-colors hover:border-blue-600 focus:border-blue-600 focus:outline-none',
        className
      )}
      type="button">
      {value ?? placeholder}
      <ArrowDown
        size={18}
        aria-hidden="true"
        className={cn('transition-transform', {
          '-rotate-180': isOpen
        })}
      />
    </button>
  )
}
